import { create } from 'zustand'

interface UIState {
  // Sidebar
  sidebarOpen: boolean
  sidebarCollapsed: boolean
  
  // Theme
  theme: 'light' | 'dark'
  
  // Loading states
  globalLoading: boolean
  
  // Modal states
  modals: Record<string, boolean>
  
  // Actions
  setSidebarOpen: (open: boolean) => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleSidebar: () => void
  toggleSidebarCollapsed: () => void
  
  setTheme: (theme: 'light' | 'dark') => void
  toggleTheme: () => void
  
  setGlobalLoading: (loading: boolean) => void
  
  openModal: (modalId: string) => void
  closeModal: (modalId: string) => void
  toggleModal: (modalId: string) => void
  isModalOpen: (modalId: string) => boolean
}

export const useUIStore = create<UIState>((set, get) => ({
  // Initial state
  sidebarOpen: true,
  sidebarCollapsed: false,
  theme: 'light',
  globalLoading: false,
  modals: {},

  // Sidebar actions
  setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),
  setSidebarCollapsed: (collapsed: boolean) => set({ sidebarCollapsed: collapsed }),
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  toggleSidebarCollapsed: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

  // Theme actions
  setTheme: (theme: 'light' | 'dark') => {
    set({ theme })
    // Apply theme to document
    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  },
  toggleTheme: () => {
    const currentTheme = get().theme
    get().setTheme(currentTheme === 'light' ? 'dark' : 'light')
  },

  // Loading actions
  setGlobalLoading: (loading: boolean) => set({ globalLoading: loading }),

  // Modal actions
  openModal: (modalId: string) => 
    set((state) => ({ 
      modals: { ...state.modals, [modalId]: true } 
    })),
  closeModal: (modalId: string) => 
    set((state) => ({ 
      modals: { ...state.modals, [modalId]: false } 
    })),
  toggleModal: (modalId: string) => 
    set((state) => ({ 
      modals: { ...state.modals, [modalId]: !state.modals[modalId] } 
    })),
  isModalOpen: (modalId: string) => get().modals[modalId] || false,
}))

// Initialize theme from localStorage
const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null
if (savedTheme) {
  useUIStore.getState().setTheme(savedTheme)
} else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
  useUIStore.getState().setTheme('dark')
}

// Listen for theme changes
useUIStore.subscribe((state) => {
  localStorage.setItem('theme', state.theme)
})